import MapView from '@arcgis/core/views/MapView';
import './map.css';
import Feature from '@arcgis/core/widgets/Feature';
import Search from '@arcgis/core/widgets/Search.js';
import { arcgisToGeoJSON } from '@terraformer/arcgis';
import type { PropsWithChildren } from 'react';
import React, { useState, useEffect, useRef } from 'react';

import {
  ObjectId,
  queryGridElementsDetails
} from '@/features/execute-calculation-set/components/Calculation/GridElementsCalculationTacticResults/useFeatureDetails';
import { useAppState } from '@/overmind';
import type { ArcgisMapServerConfig } from '@/overmind/effects/adapters/ArcgisMapAdapter/ArcgisMapAdapter';
import { mapToInternalFeature } from '@/overmind/effects/adapters/ArcgisMapAdapter/mapToInternalFeature';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import { useMap } from './MapProvider';
import { useInitializeMapView, useMapView } from './MapViewProvider';

import Graphic from '@arcgis/core/Graphic';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';

import { ConfigureLevelOfDetails } from './ZoomSettings/ConfigureLevelOfDetails';
import { MIN_SCALE_AS_BIG_COUNTRIES } from './ZoomSettings/ScalesDictionary';

import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { getBasemapSpatialReference } from '@/overmind/effects/adapters/ArcgisMapAdapter/getWebMapTitles';

export const Map: React.FunctionComponent<
  PropsWithChildren<{
    onClick: (props: {
      features: FeatureWithIDs[];
      graphics: __esri.Graphic[];
      mapInteractionLocation: __esri.ScreenPoint;
    }) => void;
    unselectFeature: boolean,
    withExtent: (payload: {
        goTo: (extent: __esri.Extent | __esri.Point | __esri.Polygon | __esri.Multipoint | __esri.Polyline) => Promise<void>;
    }) => Promise<Promise<void>>
    onViewInitialized?: (payload: {view: MapView}) => void;
  }>
> = props => {
  const mapDiv = useRef(null);
  const map = useMap();
  const initializeMapView = useInitializeMapView();
  const config = useAppState().arcgis.config.value;
  const [view, setView] = useState<MapView | null>(null);
  useEffect(() => {
    if (!config) return;
    const providedConfig = config?.mapSource;
    const adapterConfig: ArcgisMapServerConfig = {
      baseUrl: providedConfig.url,
      domainIdField: providedConfig.domainIdField,
      arcgisIdField: providedConfig.arcgisIdField,
      typeField: providedConfig.typeField,
      titleField: providedConfig.titleField,
      precision: 8,
      searchRules: providedConfig.searchRules
    };
    if (!mapDiv.current || !map) return;
    const view = new MapView({
      map,
      container: mapDiv.current,
      popupEnabled: true,
      constraints: {
        rotationEnabled: false,
        minScale: MIN_SCALE_AS_BIG_COUNTRIES,
        maxScale: 0,
        lods: ConfigureLevelOfDetails(map)
      }
    });
    initializeMapView(view);
    view.ui.add('zoom', 'top-right');
    const searchWidget = new Search({
      view
    });
    // Adds the search widget below other elements in
    // the top left corner of the view
    view.ui.add(searchWidget, {
      position: 'top-left',
      index: 2
    });

    view.on('click', event => {
      const screenPoint = (event as unknown as __esri.HitTestResult)
        .screenPoint;
      view.hitTest(screenPoint).then(response => {
        const graphicHits = response.results as unknown as __esri.GraphicHit[];
        const graphics = graphicHits
          .map(result => result.graphic)
          .filter(graphic => graphic.geometry)
        
        // highlightElement(graphics, view);
        return queryGridElementsDetails(
          graphicHits
            .filter(result => result.graphic.attributes)
            // TODO: ELVIA SPECIFIC - REMOVE AFTER DEMO!!!!!!!
            .filter(
              result =>
                result.graphic.attributes.objectid ||
                result.graphic.attributes.OBJECTID
            )
            .map(result => ({
              // TODO: ELVIA SPECIFIC - REMOVE AFTER DEMO!!!!!!!
              objectid: ObjectId.fromNumber(
                result.graphic.attributes.objectid ||
                  result.graphic.attributes.OBJECTID
              ),
              layer: result.graphic.layer
            }))
        ).then(featureSets => {
          const featuresAsGeoJson = toGeoJSONFeaturesAndLayerMap(
            featureSets,
            adapterConfig
          );

          const mappedFeatures: FeatureWithIDs[] = toInternalFeatures(
            featuresAsGeoJson,
            adapterConfig
          );

          const featureAndLayers = featureSets.flatMap(featureSet => {
            return featureSet.features
              .filter(feature => {
                const foundMappedFeature = mappedFeatures.find(
                  f =>
                    f.gc.globalId ===
                    feature.attributes[adapterConfig.domainIdField]
                );
                return foundMappedFeature ?? false;
              })
              .map(feature => {
                return {
                  internalFeature: mappedFeatures.find(
                    f =>
                      f.gc.globalId ===
                      feature.attributes[adapterConfig.domainIdField]
                  ),
                  layer: feature.layer as unknown as __esri.FeatureLayer,
                  feature
                };
              });
          });

          const titles = featureAndLayers.map(featureAndLayer => {
            const abortController = new AbortController();
            const graphic = new Graphic({
              attributes: featureAndLayer.feature.attributes,
              geometry: featureAndLayer.feature.geometry,
              layer: featureAndLayer.layer
            });

            const feature = new Feature({
              graphic,
              visibleElements: {
                title: false
              }
            });

            setTimeout(() => {
              abortController.abort();
            }, 300);

            return reactiveUtils
              .once(() => feature.title, abortController.signal)
              .then(title => ({
                internalFeature: featureAndLayer.internalFeature,
                title
              }));
          });
          Promise.allSettled(titles)
            .then(results => {
              return results
                .filter(r => r.status === 'fulfilled')
                .map(r => r.value);
            })
            .then(gridElementTitles => {
              props.onClick({
                features: gridElementTitles.map(title => {
                  const internalFeature = title.internalFeature!;
                  internalFeature.gc.webMapTitle = (title.title && title.title !== 'Unknown') ? title.title : internalFeature.gc.title;
                  return internalFeature;
                }),
                graphics,
                mapInteractionLocation: screenPoint
              });
            });
        });
      });
    });

    setView(view);
    if(props.onViewInitialized) {
      props.onViewInitialized({ view });
    }

    view.on("layerview-create", async (event) => {
      if(map.loaded) {
        await props.withExtent({ goTo: async (extent) => {
          view.goTo(extent);
        } })
      }
    })

    return () => {
      if (!view) return;
      view.map = null as any;
      view.destroy();
    };
  }, [map, initializeMapView, config]);

  useEffect(() => {
    if (!view) return;
    if (props.unselectFeature) {
      view.graphics.removeAll();
    }
  }, [props.unselectFeature, view]);

  return (
    <>
      <div id="map" ref={mapDiv} />
    </>
  );
};
export function highlightElement(graphics: Graphic[], view: MapView) {
  const graphic = graphics.find(graphic => Boolean(graphic.geometry));
  if (!graphic) return;
  const geometryType = graphic.geometry.type;
  let highlightedElement;
  const symbolColor = [0, 179, 255, 0.7];
  const outerColour = [0, 179, 255];

  if (geometryType === 'point') {
    highlightedElement = new SimpleMarkerSymbol({
      style: 'circle',
      color: symbolColor,
      size: 10,
      outline: {
        color: outerColour,
        width: 6
      }
    });
  } else if (geometryType === 'polyline') {
    highlightedElement = new SimpleLineSymbol({
      color: symbolColor,
      width: 5
    });
  } else if (geometryType === 'polygon') {
    highlightedElement = new SimpleFillSymbol({
      color: symbolColor,
      outline: {
        color: outerColour,
        width: 2
      }
    });
  }

  const highlightGraphic = new Graphic({
    geometry: graphic.geometry,
    symbol: highlightedElement
  });

  view.graphics.removeAll();
  view.graphics.add(highlightGraphic);
  // view.goTo(highlightGraphic)
}

function toInternalFeatures(
  featuresAsGeoJson: GeoJsonFeaturesAndLayerMap,
  adapterConfig: ArcgisMapServerConfig
): FeatureWithIDs<unknown>[] {
  return featuresAsGeoJson.geoJSONFeatures.flatMap(layer =>
    layer.features.map(feature => {
      const globalId = feature.properties
        ? feature.properties[adapterConfig.domainIdField]
        : null;
      const layerDetails = featuresAsGeoJson.featureToLayerMap[globalId];
      return mapToInternalFeature(
        feature as unknown as GeoJSON.Feature,
        { type: 'LAYER_DETAILS', ...layerDetails },
        adapterConfig
      );
    })
  );
}

function toGeoJSONFeatures(featureSets: __esri.FeatureSet[]) {
  return featureSets.map(feature =>
    arcgisToGeoJSON(feature)
  ) as unknown as GeoJSON.FeatureCollection[];
}

function toGeoJSONFeaturesAndLayerMap(
  featureSets: __esri.FeatureSet[],
  adapterConfig: ArcgisMapServerConfig
): GeoJsonFeaturesAndLayerMap {
  const featureToLayerMap = getFeatureToLayerMap(featureSets, adapterConfig);

  const geoJSONFeatures = toGeoJSONFeatures(featureSets);
  return { featureToLayerMap, geoJSONFeatures };
}

export type GeoJsonFeaturesAndLayerMap = {
  featureToLayerMap: FeatureToLayerMap;
  geoJSONFeatures: GeoJSON.FeatureCollection[];
};

export type FeatureToLayerMap = Record<
  string,
  { layerId: number; layerName: string }
>;

function getFeatureToLayerMap(
  featureSets: __esri.FeatureSet[],
  adapterConfig: ArcgisMapServerConfig
): FeatureToLayerMap {
  const flatFeatureToLayerMap = featureSets
    .flatMap(featureSet => {
      return featureSet.features.map(feature => {
        const isSublayer = feature.layer.type === 'subtype-sublayer';
        const layer = (
          isSublayer ? feature.layer.parent : feature.layer
        ) as __esri.FeatureLayer;

        return {
          [feature.attributes[adapterConfig.domainIdField]]: {
            layerId: layer.layerId,
            layerName: layer.title
          }
        };
      });
    })
    .reduce<FeatureToLayerMap>((acc, featureToLayerMap) => {
      return { ...acc, ...featureToLayerMap };
    }, {});

  return flatFeatureToLayerMap;
}
